import { Autocomplete, CircularProgress, FormHelperText, TextField } from "@mui/material"
import { useGetFamilyBasesQuery } from "@store/services/baseGraphFamiliesService"
import { useImmerAtom } from "jotai-immer"
import { memo, useMemo } from "react"
import { AutocompleteOnChange } from "@components/NodeLink/types"
import { linkAtom, linkDialogNodeAtom } from "@components/NodeLink/linkAtom"
import { useNodeLinkValidation } from "@components/NodeLink/useNodeLinkValidation"
import { useStore } from "@xyflow/react"
import { getGraphLinkedNodesPerVersion } from "@features/builder/helpers/nodes"
import VersionsLimitedInfo from "@components/NodeLink/VersionsLimitedInfo"
import { excludePatchVersions } from "@components/NodeLink/versionHelpers"
import { AppNode } from "@features/builder/graph/types"
import semver from "semver/preload"
import { useAtom } from "jotai"

const VersionAutocomplete = memo(() => {
  const [linkDialogNode] = useAtom(linkDialogNodeAtom)
  const [link, updateLink] = useImmerAtom(linkAtom)
  const allNodes = useStore<AppNode[]>((s) => s.nodes as AppNode[])

  const org = link.org
  const div = link.div
  const prod = link.prod
  const family = link.family

  const skip = !org || !div || !prod || !family

  const { data, isFetching } = useGetFamilyBasesQuery(
    {
      familyKey: `${org}#${div}#${prod}#${family}`,
      roleHint: "build",
    },
    { skip },
  )

  const { hasError, errorMessages } = useNodeLinkValidation(link, linkDialogNode)
  const showWarning = !!link.version && hasError

  const graphKey = org && div && prod && family ? `${org}#${div}#${prod}#${family}` : ""

  const allPossibleVersions = useMemo(
    () => excludePatchVersions(data?.map(({ version }) => version) ?? []),
    [data],
  )

  const linksPerVersion = useMemo(
    () => getGraphLinkedNodesPerVersion(allNodes, graphKey, linkDialogNode?.id ?? ""),
    [allNodes, graphKey, linkDialogNode?.id],
  )

  const hasVersionsBeenLimited = Object.keys(linksPerVersion).length > 0
  const limitedVersions = hasVersionsBeenLimited
    ? Object.keys(linksPerVersion)
    : allPossibleVersions

  const sortedOptions = limitedVersions
    .toSorted((versionA, versionB) => {
      try {
        // highest version to lowest version
        return semver.compare(versionB, versionA)
      } catch (error) {
        return -1
      }
    })
    .filter((version) => version !== "0.0.0")

  const handleChange: AutocompleteOnChange = (_event, newValue) => {
    updateLink((draft) => {
      draft.version = newValue ?? ""
    })
  }

  return (
    <>
      <Autocomplete
        options={sortedOptions}
        groupBy={() => ""} // enable grouping to show the info when versions are limited
        renderGroup={(params) => (
          <li key={params.key}>
            {hasVersionsBeenLimited && <VersionsLimitedInfo linksPerVersion={linksPerVersion} />}
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        value={link.version || null}
        onChange={handleChange}
        renderInput={(params) => <TextField {...params} label="Version" error={!!showWarning} />}
        disabled={skip}
        isOptionEqualToValue={(option, value) => option === value}
        loading={isFetching}
        loadingText={<CircularProgress size={20} />}
      />
      {showWarning &&
        errorMessages.map((err, idx) => (
          <FormHelperText key={idx} error>
            - {err}
          </FormHelperText>
        ))}
    </>
  )
})
VersionAutocomplete.displayName = "VersionAutocomplete"

export default VersionAutocomplete
